import React, { useState, Fragment, useEffect } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Table, notification,
} from 'antd';
import { QUANTITY, toISTDate } from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import H3FormInput from '@Uilib/h3FormInput';
import MrpV2Actions from '@Actions/production/mrpV2Actions';
import ReservationActions from '@Actions/reservationActions';
import './style.scss';
import HideValue from '../../../../../Common/RestrictedAccess/HideValue';

const EditPurchaseLinkAndUnlink = ({
  tenantId, mrpData, PoData, callback, productSkuIds, unlinkLinkedPoToMrp, unlinkLinkedPoToMrpLoading, LinkExistingPoToMrpV2, stockItemsShortage,
  getLinkExistingPoToMrp, MONEY, getLinkExistingPoToMrpLoading, getAlreadyLinkedPoToMrp, updateLinkedPoToMrp, updateLinkedPoToMrpLoading, loading, priceMasking,
}) => {
  const [call, setCall] = useState(false);
  const [linkedPoToProductData, setLinkedPoToProductData] = useState([]);
  const [unlinkedPoToProductData, setUnlinkedPoToProductData] = useState([]);
  const [formSubmittedUnlinkedQty, setFormSubmittedUnlinkedQty] = useState(false);
  const [formSubmittedLinkedQty, setFormSubmittedLinkedQty] = useState(false);
  const pendingQuantity = stockItemsShortage?.data?.find((item) => item.product_sku_id === PoData?.product_sku_id)?.pending_quantity || 0;

  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;
  // use effect for getting unlinked po lines
  useEffect(() => {
    const payloadForUnlinkedPo = {
      tenantId,
      productSkuIds: productSkuIds || null,
    };
    getLinkExistingPoToMrp(payloadForUnlinkedPo, (PoDataProductWise) => {
      const newPOData = PoDataProductWise?.flatMap((po) => po?.po_lines?.map((poLine) => ({
        ...poLine,
        // Add any additional properties or transformations here if needed
        linked_quantity: '',
      })));
      setUnlinkedPoToProductData(newPOData);
      setFormSubmittedUnlinkedQty(false);
      callback();
    });
  }, [call]);

  // use effect to get linked po to mrp
  useEffect(() => {
    const payloadForLinkedPo = {
      mrpId: mrpData?.mrp_id,
      productSkuIds: productSkuIds || null,
    };
    getAlreadyLinkedPoToMrp(payloadForLinkedPo, (PoDataProductWise) => {
      const newPOData = PoDataProductWise?.flatMap((po) => po?.po_lines?.map((poLine) => ({
        ...poLine,
        originalAvailableQuantity: Number(poLine.available_quantity) + Number(poLine.linked_quantity),
        originalLinkedQuantity: Number(poLine.linked_quantity),
        new_linked_quantity: Number(poLine.linked_quantity) - Number(poLine.received_quantity),
      })));
      setLinkedPoToProductData(newPOData);
      setFormSubmittedLinkedQty(false);
      callback();
    });
  }, [call]);

  // function to handle error based on quantity is greater than available or ordered quantity
  const handleErrorForUnlinkedPos = (lineData) => {
    if (Number(lineData.linked_quantity) > Number(pendingQuantity)) {
      return 'Quantity should be less than or equal to Ordered Qty';
    } if (Number(lineData.linked_quantity) > Number(lineData.available_quantity)) {
      return 'Quantity should be less than or equal to Available Qty';
    }
    return '';
  };

  // function to handle error based on quantity is greater than available or ordered quantity and exclude the linked quantity
  const handleErrorForLinkedPos = (lineData) => {
    if (Number(lineData.new_linked_quantity) > Number(pendingQuantity + lineData.originalLinkedQuantity)) {
      return 'Quantity should be less than or equal to Ordered Qty';
    } if (Number(lineData.new_linked_quantity) > Number(lineData.originalAvailableQuantity)) {
      return 'Quantity should be less than or equal to Available Qty';
    }
    return '';
  };

  const getUnlinkedPoColumns = () => {
    const poListColumns = [
      {
        title: 'Purchase Order#',
        dataIndex: 'po_number',
        render: (text, record) => (
          <div
            className="list__actions"
            style={{
              color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
            }}
            onClick={() => window.open(`/approval?type=po&id=${record?.po_id}`)}
          >
            {record?.po_number}
          </div>
        ),
        fixed: 'left',
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Vendor',
        render: (text, record) => (
          <Fragment>
            <div
              style={{
                color: 'rgb(106, 106, 106)',
                fontWeight: '600',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            />
            <div
              style={{
                color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
              }}
              onClick={() => window.open(`/vendors/view/${record?.seller_info?.seller_id}`)}
            >
              {(record && record.seller_info) ? `${record?.seller_info?.internal_slr_code ? `${record?.seller_info?.internal_slr_code} - ` : ''}${record.seller_info?.seller_name}`?.trim() : ''}
            </div>
            <div>
              {record?.seller_address?.city ? record.seller_address.city : ''}
            </div>
          </Fragment>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Delivery Date',
        render: (text, record) => (
          <div>
            {toISTDate(record?.edd).format('DD/MM/YYYY')}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Offer Price',
        render: (text, record) => (
          (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view offer price"} /> : `${MONEY((record.offer_price || '0'), record?.org_currency_info?.currency_code)} / (${record?.purchase_uom_info?.[0]?.uqc || ''})`
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Purchased Quantity',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Available Quantity',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.available_quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>

        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Quantity to link',
        fixed: 'right',
        render: (record) => {
          const errorCondition = (formSubmittedUnlinkedQty === record?.po_line_id && Number(record?.linked_quantity) <= 0) || handleErrorForUnlinkedPos(record);
          const error = handleErrorForUnlinkedPos(record);
          return (
            <div style={{ width: '70px' }}>
              <H3FormInput
                value={record?.linked_quantity}
                type="number"
                inputClassName={`orgFormInput ${errorCondition ? 'orgFormInputError' : ''} `}
                onChange={(e) => {
                  const copyData = unlinkedPoToProductData?.map((item) => {
                    if (record.po_line_id === item.po_line_id) {
                      item.linked_quantity = e.target.value;
                    }
                    return item;
                  });
                  setUnlinkedPoToProductData(copyData);
                }}
              />
              {error && (
                <div
                  style={{
                    color: 'red',
                    fontSize: '11px',
                    marginTop: '2px',
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          );
        },
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: '',
        key: 'action',
        render: (text, record) => (
          <div className="list__actions">
            <div
              className="list__actions-button list__actions-button-dark"
              style={{ width: '60px', whiteSpace: 'nowrap' }}
              onClick={() => {
                setFormSubmittedUnlinkedQty(record.po_line_id);
                const payload = {
                  po_line_id: record.po_line_id,
                  quantity: Number(record.linked_quantity),
                  po_id: record.po_id,
                };
                if (Number(record.linked_quantity) > 0 && !handleErrorForUnlinkedPos(record)) {
                  LinkExistingPoToMrpV2({
                    mrp_id: mrpData?.mrp_id,
                    po_lines: [payload],
                  }, () => {
                    setCall(!call);
                  });
                } else if (Number(record.linked_quantity) <= 0) {
                  notification.error({
                    message: 'Quantity to link should be greater than 0',
                    duration: 3,
                    placement: 'top',
                  });
                }
              }}
            >
              Link
            </div>
          </div>
        ),
        fixed: 'right',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        className: 'list__actions-column',
      },
    ];
    return poListColumns;
  };

  const getLinkedPoColumns = () => {
    const poListColumns = [
      {
        title: 'Purchase Order#',
        dataIndex: 'po_number',
        render: (text, record) => (
          <div
            className="list__actions"
            style={{
              color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
            }}
            onClick={() => window.open(`/approval?type=po&id=${record?.po_id}`)}
          >
            {record?.po_number}
          </div>
        ),
        fixed: 'left',
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Vendor',
        render: (text, record) => (
          <Fragment>
            <div
              style={{
                color: 'rgb(106, 106, 106)',
                fontWeight: '600',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            />
            <div
              style={{
                color: '#2D7DF7', fontWeight: '600', fontSize: '12px', cursor: 'pointer',
              }}
              onClick={() => window.open(`/vendors/view/${record?.seller_info?.seller_id}`)}
            >
              {(record && record.seller_info) ? `${record?.seller_info?.internal_slr_code ? `${record?.seller_info?.internal_slr_code} - ` : ''}${record.seller_info?.seller_name}`?.trim() : ''}
            </div>
            <div>
              {record?.seller_address?.city ? record.seller_address.city : ''}
            </div>
          </Fragment>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Delivery Date',
        render: (text, record) => (
          <div>
            {toISTDate(record?.edd).format('DD/MM/YYYY')}
          </div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Offer Price',
        render: (text, record) => (
          (isDataMaskingPolicyEnable && isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view cost price"} /> : `${MONEY((record.offer_price || '0'), record?.org_currency_info?.currency_code)} / (${record?.purchase_uom_info?.[0]?.uqc || ''})`
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Purchased Quantity',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Linked Quantity',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.linked_quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>
        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Available To Link',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.available_quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>

        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Received Quantity',
        render: (text, record) => (
          <div>{`${QUANTITY(record?.received_quantity || 0, record?.uom_info?.precision)} ${record?.uom_info?.uqc?.toProperCase() || ''}`}</div>

        ),
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: 'Pending From Vendor',
        fixed: 'right',
        render: (record) => {
          const errorCondition = (formSubmittedLinkedQty === record?.po_line_id && Number(record?.new_linked_quantity) <= 0) || handleErrorForLinkedPos(record);
          const error = handleErrorForLinkedPos(record);
          return (
            <div style={{ width: '70px' }}>
              <H3FormInput
                value={record?.new_linked_quantity}
                type="number"
                inputClassName={`orgFormInput ${errorCondition ? 'orgFormInputError' : ''} `}
                onChange={(e) => {
                  const copyData = linkedPoToProductData?.map((item) => {
                    if (record.po_line_id === item.po_line_id) {
                      item.new_linked_quantity = e.target.value;
                    }
                    return item;
                  });
                  setLinkedPoToProductData(copyData);
                }}
              />
              {error && (
                <div
                  style={{
                    color: 'red',
                    fontSize: '11px',
                    marginTop: '2px',
                  }}
                >
                  {error}
                </div>
              )}
            </div>
          );
        },
        responsive: ['sm', 'md', 'lg', 'xxl'],
      },
      {
        title: '',
        key: 'action',
        render: (text, record) => (
          <div className="list__actions">
            <div
              className="list__actions-button list__actions-button-dark"
              style={{ width: '70px', whiteSpace: 'nowrap', cursor: 'pointer', backgroundColor: 'transparent' }}
              onClick={() => {
                setFormSubmittedLinkedQty(record.po_line_id);
                const payload = {
                  po_line_id: record.po_line_id,
                  quantity: Number(record.new_linked_quantity),
                  mrp_id: mrpData?.mrp_id,
                };
                if (Number(record.new_linked_quantity) > 0 && !handleErrorForLinkedPos(record)) {
                  updateLinkedPoToMrp(payload, () => {
                    setCall(!call);
                  });
                } else if (Number(record.new_linked_quantity) <= 0) {
                  notification.error({
                    message: 'Quantity to link should be greater than 0',
                    duration: 3,
                    placement: 'top',
                  });
                }
              }}
            >
              Update
            </div>
          </div>
        ),
        fixed: 'right',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        className: 'list__actions-column',
      },
      {
        title: '',
        key: 'action',
        render: (text, record) => (
          <div className="list__actions">
            <div
              className="list__actions-button list__actions-button-dark"
              style={{
                width: '65px',
                whiteSpace: 'nowrap',
                color: Number(record.received_quantity) > 0 ? 'grey' : 'red',
                cursor: 'pointer',
                backgroundColor: 'transparent',
              }}
              onClick={() => {
                const payload = {
                  mrp_id: mrpData?.mrp_id,
                  po_line_ids: `${record.po_line_id}`,
                };

                if (Number(record.received_quantity) > 0) {
                  notification.error({
                    message: 'Received Quantity is greater than 0, cannot unlink',
                    duration: 3,
                    placement: 'top',
                  });
                  return;
                }
                unlinkLinkedPoToMrp(payload, () => {
                  setCall(!call);
                });
              }}
            >
              Unlink
            </div>
          </div>
        ),
        fixed: 'right',
        responsive: ['sm', 'md', 'lg', 'xxl'],
        className: 'list__actions-column',
      },
    ];
    return poListColumns;
  };
  return (
    <Fragment>
      <div className="reserve-stock-item__wrapper">
        <div className="reserve-stock-item__header-wrapper">
          <Link to={`/inventory/product/view/${PoData?.product_sku_id}?tab=1`} target="_blank">
            <H3Text text={`#${PoData?.internal_sku_code} ${PoData?.product_sku_name}`} className="reserve-stock-item__product-name" />
          </Link>
          &nbsp;
          <H3Text text={`To be ordered - ${pendingQuantity} `} />
        </div>
        <div className="reserve-stock-item__body-wrapper">
          <div className="reserve-stock-item__table-lower">
            <Table
              title={() => (
                <div className="material-shortage__table-title__wrapper">
                  <H3Text text={`${linkedPoToProductData?.length || '0'} Linked Purchase Orders`} className="" />
                </div>
              )}
              bordered
              loading={getLinkExistingPoToMrpLoading || updateLinkedPoToMrpLoading || loading}
              showHeader
              size="small"
              scroll={{ x: 'max-content' }}
              dataSource={linkedPoToProductData || []}
              columns={getLinkedPoColumns()}
              pagination={false}
            />
          </div>
        </div>
        <div className="reserve-stock-item__body-wrapper">
          <div className="reserve-stock-item__table-lower">
            <Table
              title={() => (
                <div className="material-shortage__table-title__wrapper">
                  <H3Text text={`${unlinkedPoToProductData?.length || '0'} Unlinked Purchase Orders`} className="" />
                </div>
              )}
              bordered
              loading={unlinkLinkedPoToMrpLoading || getLinkExistingPoToMrpLoading || loading}
              showHeader
              size="small"
              scroll={{ x: 'max-content' }}
              dataSource={unlinkedPoToProductData || []}
              columns={getUnlinkedPoColumns()}
              pagination={false}
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, MrpV2Reducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  getLinkExistingPoToMrpLoading: MrpV2Reducers.getLinkExistingPoToMrpLoading,
  updateLinkedPoToMrpLoading: MrpV2Reducers.updateLinkedPoToMrpLoading,
  unlinkLinkedPoToMrpLoading: MrpV2Reducers.unlinkLinkedPoToMrpLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getLinkExistingPoToMrp: (payload, callback) => dispatch(MrpV2Actions.getLinkExistingPoToMrp(payload, callback)),
  createReservation: (payload, callback) => dispatch(ReservationActions.createReservation(payload, callback)),
  updateFreeReservation: (payload, callback) => dispatch(ReservationActions.updateFreeReservation(payload, callback)),
  getAlreadyLinkedPoToMrp: (payload, callback) => dispatch(MrpV2Actions.getAlreadyLinkedPoToMrp(payload, callback)),
  unlinkLinkedPoToMrp: (payload, callback) => dispatch(MrpV2Actions.unlinkLinkedPoToMrp(payload, callback)),
  updateLinkedPoToMrp: (payload, callback) => dispatch(MrpV2Actions.updateLinkedPoToMrp(payload, callback)),
  LinkExistingPoToMrpV2: (payload, callback) => dispatch(MrpV2Actions.LinkExistingPoToMrpV2(payload, callback)),
});

EditPurchaseLinkAndUnlink.propTypes = {
  PoData: PropTypes.any,
  callback: PropTypes.any,
  productSkuIds: PropTypes.any,
  unlinkLinkedPoToMrp: PropTypes.any,
  unlinkLinkedPoToMrpLoading: PropTypes.any,
  LinkExistingPoToMrpV2: PropTypes.any,
  stockItemsShortage: PropTypes.any,
  getLinkExistingPoToMrp: PropTypes.any,
  MONEY: PropTypes.any,
  getLinkExistingPoToMrpLoading: PropTypes.any,
  getAlreadyLinkedPoToMrp: PropTypes.any,
  updateLinkedPoToMrp: PropTypes.any,
  updateLinkedPoToMrpLoading: PropTypes.any,
  loading: PropTypes.any,
  mrpData: PropTypes.any,
  tenantId: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(EditPurchaseLinkAndUnlink));
